/**
 * 스크롤 위치 복원 컴포넌트
 * - 브라우저 뒤로/앞으로 시 스크롤 위치 복원
 * - 링크 클릭(앞으로 이동) 시 스크롤 상단 초기화
 * - 새로고침 시 스크롤 위치 유지
 */
import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const SCROLL_STORAGE_KEY = 'safetyhub-scroll-positions';

// 스크롤 위치 저장소 타입
interface ScrollPositions {
  [key: string]: number;
}

// 스크롤 위치 가져오기
function getScrollPositions(): ScrollPositions {
  try {
    const stored = sessionStorage.getItem(SCROLL_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

// 스크롤 위치 저장
function saveScrollPosition(key: string, position: number) {
  try {
    const positions = getScrollPositions();
    if (position > 0) {
      positions[key] = position;
    } else {
      delete positions[key];
    }
    sessionStorage.setItem(SCROLL_STORAGE_KEY, JSON.stringify(positions));
  } catch {
    // sessionStorage 사용 불가 시 무시
  }
}

// 스크롤 위치 읽기
function getSavedScrollPosition(key: string): number {
  const positions = getScrollPositions();
  return positions[key] ?? 0;
}

export function ScrollRestoration() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const key = location.pathname + location.search;

  // 현재 키를 ref로 저장 (cleanup에서 사용)
  const keyRef = useRef(key);
  keyRef.current = key;

  useEffect(() => {
    // 브라우저 기본 스크롤 복원 비활성화
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

    // 네비게이션 타입에 따른 스크롤 동작
    // - POP: 브라우저 뒤로/앞으로 → 저장된 위치로 복원
    // - PUSH/REPLACE: 링크 클릭, 리다이렉트 → 상단으로 초기화
    const shouldRestore = navigationType === 'POP';

    if (!shouldRestore) {
      // 앞으로 이동 시 상단으로
      window.scrollTo(0, 0);
      return;
    }

    // POP 네비게이션: 저장된 위치로 복원
    const savedPosition = getSavedScrollPosition(key);
    if (savedPosition === 0) return;

    // ⚠️ "목표에 닿았으니 끝"으로 처리하면 안 된다. 복원 직후에도 레이아웃은 계속 정착하고,
    // 그때 문서 높이가 바뀌면 브라우저 스크롤 앵커링이 위치를 끌어당긴다
    // (2026-07-23 실측: 문서 4582에서 1368로 복원 성공 → 7157로 정착하며 842로 밀림).
    // 그래서 완료 판정을 없애고, **높이가 바뀔 때마다 다시 맞춘다**. 종료는 아래 3초 타임아웃이 맡는다.
    let isRestored = false;

    // 사용자가 직접 스크롤을 시작하면 즉시 손을 뗀다 — 안 그러면 최대 3초간 조작과 다툰다.
    // 'scroll'이 아니라 입력 이벤트를 듣는다(우리가 부르는 scrollTo도 scroll을 발생시키므로).
    const USER_INPUT = ['wheel', 'touchstart', 'keydown'] as const;
    const abort = () => {
      isRestored = true;
      resizeObserver?.disconnect();
    };
    USER_INPUT.forEach((e) => window.addEventListener(e, abort, { passive: true, once: true }));

    const restoreScroll = () => {
      if (isRestored) return;

      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      if (maxScroll < savedPosition - 100) return;
      if (Math.abs(window.scrollY - savedPosition) < 2) return;

      window.scrollTo(0, savedPosition);
    };

    // ResizeObserver로 콘텐츠 로드 감지
    const resizeObserver: ResizeObserver | null = new ResizeObserver(() => {
      restoreScroll();
    });
    resizeObserver.observe(document.body);
    restoreScroll();

    // 폴백 타임아웃
    const timeout = setTimeout(() => {
      if (!isRestored) {
        restoreScroll();
        resizeObserver?.disconnect();
        isRestored = true;
      }
    }, 3000);

    return () => {
      resizeObserver?.disconnect();
      clearTimeout(timeout);
      USER_INPUT.forEach((e) => window.removeEventListener(e, abort));
    };
  }, [key, navigationType]);

  // 페이지 떠날 때 스크롤 위치 저장 (별도 effect)
  //
  // ⚠️ cleanup에서 저장하면 안 된다. 이 컴포넌트는 App 최상단(Routes 밖)이라 SPA 이동으로는
  // 언마운트되지 않고, 실제로 cleanup이 도는 건 **StrictMode의 effect 이중 호출**(dev)뿐이다.
  // 그때 scrollY는 갓 로드된 0이라 saveScrollPosition이 저장값을 지워버려(=position>0 아니면 delete)
  // 새로고침 복원이 통째로 무력화됐다(2026-07-23 실측: 저장 1368 → 로드 중 cleanup이 {}로 삭제).
  // 그래서 이탈 저장은 실제 언로드 이벤트에만 건다. pagehide는 bfcache·모바일 백그라운드 전환까지 덮는다.
  useEffect(() => {
    const save = () => {
      saveScrollPosition(keyRef.current, window.scrollY);
    };
    window.addEventListener('beforeunload', save);
    window.addEventListener('pagehide', save);

    return () => {
      window.removeEventListener('beforeunload', save);
      window.removeEventListener('pagehide', save);
    };
  }, []);

  return null;
}

export default ScrollRestoration;
